import {
  CaretLeftIcon,
  CaretRightIcon,
  ShootingStarIcon,
} from '@phosphor-icons/react'
import axios from 'axios'
import clsx from 'clsx'
import { format, getDay, isSameDay, setDay } from 'date-fns'
import { useMemo } from 'react'
import useSWR from 'swr'
import { MyBLINK } from '@/src/@types/blinkadmin'
import { fullFlchUrl } from '@/src/BlinkAdminApiClient'
import Button from '@/src/components/core/Button'
import { formatGerman } from '@/src/components/core/lib/date'
import { cn } from '@/src/components/core/lib/utils'
import {
  BookingState,
  MergedSlot,
  useBookingContext,
} from '@/src/components/flchBooking/instructorContext/BookingContext'
import SlotItem from '@/src/components/flchBooking/instructorContext/SlotItem'

export default function Slots({ step }: { step: 'halfWeek' | 'week' }) {
  const {
    instructor,
    location,
    address,
    gearbox,
    config,
    changeSelectedDate,
    setSelectedDate,
  } = useBookingContext()

  const createQueryParams = () => {
    let correctedDate: Date
    if (step === 'week') {
      correctedDate = setDay(config.selectedDate, 1)
    } else {
      const weekday =
        getDay(config.selectedDate) === 0 ? 7 : getDay(config.selectedDate)
      correctedDate = setDay(config.selectedDate, weekday < 4 ? 1 : 4)
    }
    let params = ''
    params += `users[]=${instructor!.id}&`
    params += `gearbox=${gearbox}&`
    params += `week=${format(correctedDate, 'yyyy-MM-dd')}&`
    params += `address=${address!.id}`
    return params
  }

  const { data: slots } = useSWR<MyBLINK.BookableSlot[]>(
    location &&
      address &&
      gearbox &&
      instructor &&
      `booking/locations/${location.id}/slots?${createQueryParams()}`,
    async () =>
      (
        await axios.get(
          fullFlchUrl(
            `booking/locations/${location!.id}/slots?${createQueryParams()}`
          )
        )
      ).data
  )

  const mergedSlots = useMemo(() => {
    if (!slots) return
    const mergedSlots: MergedSlot[] = []
    for (const slot of slots) {
      const existingSlot = mergedSlots.find((s) => s.start === slot.start)
      if (existingSlot) {
        existingSlot.id = `${existingSlot.id}-${slot.id}`
        existingSlot.userIds.push(slot.user)
        existingSlot.isFavored.push(slot.isFavored)
      } else {
        mergedSlots.push({
          id: slot.id,
          start: slot.start,
          end: slot.end,
          userIds: [slot.user],
          isFavored: [slot.isFavored],
        })
      }
    }
    return mergedSlots.sort(
      (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
    )
  }, [slots])

  const hasFavoriteSlots = useMemo(() => {
    if (!mergedSlots) return false
    return mergedSlots
      .filter((slot) => {
        if (step === 'week') return true
        return (
          isSameDay(
            new Date(slot.start),
            getDateFromIndex(config.selectedDate, 0, step)
          ) ||
          isSameDay(
            new Date(slot.start),
            getDateFromIndex(config.selectedDate, 1, step)
          ) ||
          isSameDay(
            new Date(slot.start),
            getDateFromIndex(config.selectedDate, 2, step)
          )
        )
      })
      .some((slot) => slot.isFavored.some((favored) => favored))
  }, [config.selectedDate, mergedSlots, step])

  return (
    <>
      {instructor && (
        <p className="mt-2">
          Wähle eine freie Lektion (45 min) bei{' '}
          <span className="font-bold">{instructor.name}</span> aus.
        </p>
      )}
      <div className="mt-8 flex items-center space-x-2">
        <button
          className={cn(
            'rounded-sm p-2 text-blue-600 active:bg-blue-50 lg:hover:text-blue-800',
            {
              'text-blue-400 active:bg-transparent lg:hover:text-blue-400':
                config.state < BookingState.InstructorSelected,
            }
          )}
          onClick={() => {
            changeSelectedDate(step, 'before')
          }}
          disabled={config.state < BookingState.GearboxSelected}
        >
          <CaretLeftIcon weight="bold" size={20} />
        </button>
        <button
          onClick={() => setSelectedDate(new Date())}
          className="hidden font-bold text-blue-700 active:text-blue-800 md:block lg:hover:text-blue-800"
        >
          Heute
        </button>
        <button
          className={cn(
            'rounded-sm p-2 text-blue-600 active:bg-blue-50 lg:hover:text-blue-800',
            {
              'text-blue-400 active:bg-transparent lg:hover:text-blue-400':
                config.state < BookingState.GearboxSelected,
            }
          )}
          onClick={() => {
            changeSelectedDate(step, 'next')
          }}
          disabled={config.state < BookingState.GearboxSelected}
        >
          <CaretRightIcon weight="bold" size={20} />
        </button>
        <p className="font-bold">
          {formatGerman(config.selectedDate, 'MMMM yyyy')}
        </p>
      </div>
      <div className="-mx-2 mt-8 grid grid-cols-3 divide-x divide-blue-100 @6xl:grid-cols-6">
        {[...Array(step === 'halfWeek' ? 3 : 6)].map((_, dayIndex) => (
          <div key={dayIndex} className="px-2">
            <div className="sticky top-18.5 z-10 flex flex-col items-center bg-white pb-3 text-center lg:top-0">
              <p
                className={clsx(
                  'flex size-8 items-center justify-center rounded-full',
                  {
                    'bg-blue-100': isSameDay(
                      new Date(),
                      getDateFromIndex(config.selectedDate, dayIndex, step)
                    ),
                  }
                )}
              >
                {formatGerman(
                  getDateFromIndex(config.selectedDate, dayIndex, step),
                  'cccccc'
                )}
              </p>
              <p className="font-bold">
                {formatGerman(
                  getDateFromIndex(config.selectedDate, dayIndex, step),
                  'dd.MM'
                )}
              </p>
            </div>
            <div className="space-y-3">
              {config.state < BookingState.GearboxSelected && (
                <>
                  {[...Array(hashedNumber(config.selectedDate, dayIndex))].map(
                    (_, i) => (
                      <div
                        key={i}
                        className="h-11 rounded-sm bg-gray-200"
                      ></div>
                    )
                  )}
                </>
              )}
              {config.state >= BookingState.GearboxSelected && !slots && (
                <>
                  {[...Array(hashedNumber(config.selectedDate, dayIndex))].map(
                    (_, i) => (
                      <div
                        key={i}
                        className="h-11 animate-pulse rounded-sm bg-gray-200 duration-1000"
                      ></div>
                    )
                  )}
                </>
              )}
              {config.state >= BookingState.GearboxSelected && mergedSlots && (
                <>
                  {mergedSlots
                    .filter((s) =>
                      isSameDay(
                        new Date(s.start),
                        getDateFromIndex(config.selectedDate, dayIndex, step)
                      )
                    )
                    .map((slot) => (
                      <SlotItem
                        key={slot.id}
                        slot={slot}
                        slots={slots}
                        step={step}
                      />
                    ))}
                  {mergedSlots.filter((s) =>
                    isSameDay(
                      new Date(s.start),
                      getDateFromIndex(config.selectedDate, dayIndex, step)
                    )
                  ).length === 0 && (
                    <div className="flex h-11 items-center justify-center rounded-sm bg-gray-50 text-sm text-gray-600">
                      Ausgebucht
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      {hasFavoriteSlots && instructor && (
        <p className="mt-8 flex space-x-2">
          <ShootingStarIcon
            size={20}
            weight="fill"
            className="mt-0.5 shrink-0 text-blue-600"
          />
          <span>Diese Termine passen {instructor.name} am besten.</span>
        </p>
      )}
      {mergedSlots?.filter((slot) => {
        if (step === 'week') return true
        return (
          isSameDay(
            new Date(slot.start),
            getDateFromIndex(config.selectedDate, 0, step)
          ) ||
          isSameDay(
            new Date(slot.start),
            getDateFromIndex(config.selectedDate, 1, step)
          ) ||
          isSameDay(
            new Date(slot.start),
            getDateFromIndex(config.selectedDate, 2, step)
          )
        )
      }).length === 0 &&
        instructor && (
          <div className="mt-8 bg-blue-50 p-5">
            <p className="hidden @6xl:block">
              {instructor.name} hat in dieser Woche keine freien Termine.
            </p>
            <p className="@6xl:hidden">
              {instructor.name} hat an den angezeigten Tagen keine freien
              Termine.
            </p>
            <div className="mt-4 grid gap-4 @xl:flex">
              <Button
                version="blue"
                onClick={() => {
                  changeSelectedDate(step, 'next')
                }}
                className="hidden @6xl:block"
              >
                Zur nächsten Woche
              </Button>
              <Button
                version="blue"
                onClick={() => {
                  changeSelectedDate(step, 'next')
                }}
                className="@6xl:hidden"
              >
                Nächste Tage anzeigen
              </Button>
            </div>
          </div>
        )}
    </>
  )
}

const getDateFromIndex = (
  date: Date,
  dayIndex: number,
  step: 'halfWeek' | 'week'
) => {
  const weekday = getDay(date) === 0 ? 7 : getDay(date)
  if (step === 'halfWeek' && weekday > 3) {
    return setDay(date, dayIndex + 4)
  } else {
    return setDay(date, dayIndex + 1)
  }
}

const hashedNumber = (selectedDate: Date, dayIndex: number) => {
  const time = selectedDate.getTime() / 1000 / 60 / 60 / 24
  return Math.round(((time * (dayIndex + 2)) % 3) + 2)
}
